/**
 * ZAXIA Guest – My Room View
 * Phase P2.9-2: Fault reporter — tap an issue to notify maintenance instantly
 */

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Droplets,
  HelpCircle,
  Lightbulb,
  Loader2,
  Lock,
  Monitor,
  RefreshCw,
  Thermometer,
  Trash2,
  Waves,
  Wifi,
  Wrench,
  X,
} from 'lucide-react';

import { createGuestRequest, deleteGuestRequest, GuestRequestCategory, isRequestResolved } from '@/api';
import type { ApiGuestRequest } from '@/api';
import { useMyRequests } from '@/hooks/useMyRequests';
import { useData } from '@/providers/DataProvider';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MyRequestsViewRef {
  handleVoiceCommand: (command: {
    type:
      | 'NEXT_COLUMN'
      | 'PREV_COLUMN'
      | 'GO_TO_COLUMN'
      | 'NEXT_REQUEST'
      | 'PREV_REQUEST'
      | 'FIRST_REQUEST'
      | 'LAST_REQUEST'
      | 'GO_TO_INDEX'
      | 'EXPAND'
      | 'COLLAPSE'
      | 'READ_REQUEST'
      | 'COUNT'
      | 'STATUS'
      | 'CANCEL_REQUEST'
      | 'REPORT_FAULT'
      | 'CONFIRM_SUBMIT'
      | 'DISMISS_SUBMIT'
      | 'BACK';
    payload?: unknown;
  }) => string | void;
}

interface MyRequestsViewProps {
  onClose: () => void;
  onRequestsVoiceCommandReady?: (
    handler: MyRequestsViewRef['handleVoiceCommand']
  ) => void;
}

// ---------------------------------------------------------------------------
// Fault category definitions
// ---------------------------------------------------------------------------

interface FaultCategory {
  id: string;
  label: string;
  hint: string;
  description: string; // sent to backend as request description
  icon: React.ElementType;
  iconColor: string;
}

const FAULT_CATEGORIES: FaultCategory[] = [
  {
    id: 'ac-heating',
    label: 'AC / Heating',
    hint: 'Temperature or climate',
    description: 'AC / Heating issue',
    icon: Thermometer,
    iconColor: 'text-blue-400',
  },
  {
    id: 'hot-water',
    label: 'Hot Water',
    hint: 'No hot water',
    description: 'Hot water issue',
    icon: Droplets,
    iconColor: 'text-cyan-400',
  },
  {
    id: 'plumbing',
    label: 'Shower / Toilet',
    hint: 'Bathroom plumbing',
    description: 'Shower / Toilet issue',
    icon: Waves,
    iconColor: 'text-cyan-300',
  },
  {
    id: 'lighting',
    label: 'Lighting',
    hint: 'Lights not working',
    description: 'Lighting issue',
    icon: Lightbulb,
    iconColor: 'text-yellow-400',
  },
  {
    id: 'tv',
    label: 'TV',
    hint: 'Screen or remote',
    description: 'TV / Screen not working',
    icon: Monitor,
    iconColor: 'text-slate-400',
  },
  {
    id: 'wifi',
    label: 'Wi-Fi',
    hint: 'Internet connectivity',
    description: 'Internet / Wi-Fi issue',
    icon: Wifi,
    iconColor: 'text-blue-400',
  },
  {
    id: 'door-lock',
    label: 'Door / Lock',
    hint: 'Key card or lock',
    description: 'Door / Lock issue',
    icon: Lock,
    iconColor: 'text-orange-400',
  },
  {
    id: 'furniture',
    label: 'Furniture',
    hint: 'Damaged or broken',
    description: 'Furniture / Equipment issue',
    icon: AlertTriangle,
    iconColor: 'text-amber-400',
  },
  {
    id: 'other',
    label: 'Other',
    hint: 'Anything else',
    description: 'Other room issue',
    icon: HelpCircle,
    iconColor: 'text-slate-400',
  },
];

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<string, string> = {
  NEW: 'Reported',
  IN_PROGRESS: 'Being handled',
  COMPLETED: 'Resolved',
};

const STATUS_PILL: Record<string, string> = {
  NEW: 'text-amber-400 bg-amber-900/30 border border-amber-700/50',
  IN_PROGRESS: 'text-blue-400 bg-blue-900/30 border border-blue-700/50',
  COMPLETED: 'text-emerald-400 bg-emerald-900/30 border border-emerald-700/50',
};

function getFaultDisplay(req: ApiGuestRequest): {
  icon: React.ElementType;
  label: string;
  iconColor: string;
} {
  const fc = FAULT_CATEGORIES.find((c) => c.description === req.description);
  return fc
    ? { icon: fc.icon, label: fc.label, iconColor: fc.iconColor }
    : { icon: Wrench, label: 'Room Issue', iconColor: 'text-slate-400' };
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface FaultTileProps {
  fault: FaultCategory;
  submitting: boolean;
  justSent: boolean;
  remoteFocused: boolean;
  isPending: boolean;
  onTap: () => void;
}

const FaultTile: React.FC<FaultTileProps> = ({ fault, submitting, justSent, remoteFocused, isPending, onTap }) => {
  const Icon = fault.icon;
  return (
    <button
      onClick={onTap}
      onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
      disabled={submitting || justSent}
      className={[
        'flex flex-col items-center justify-center gap-1',
        'px-3 py-5 rounded-none border transition-all duration-200 select-none',
        'active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed',
        remoteFocused
          ? 'bg-white/15 border-white/50 ring-2 ring-white/40'
          : justSent
          ? 'bg-emerald-900/30 border-emerald-700/50'
          : isPending
          ? 'bg-amber-900/25 border-amber-600/50 ring-1 ring-amber-500/40'
          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20',
      ].join(' ')}
      aria-label={`Report ${fault.label}`}
    >
      {justSent ? (
        <CheckCircle className="w-5 h-5 text-emerald-400" />
      ) : submitting ? (
        <Loader2 className="w-5 h-5 animate-spin text-white/50" />
      ) : (
        <Icon className={`w-5 h-5 ${fault.iconColor}`} />
      )}
      <span className="font-medium text-[11px] text-white/90 text-center leading-tight">
        {justSent ? 'Sent' : fault.label}
      </span>
    </button>
  );
};

interface TimelineItemProps {
  req: ApiGuestRequest;
  focused: boolean;
  cancelPending: boolean;
  cancelling: boolean;
  onRequestCancel: () => void;
  onCancelConfirm: () => void;
  onCancelDismiss: () => void;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
  req,
  focused,
  cancelPending,
  cancelling,
  onRequestCancel,
  onCancelConfirm,
  onCancelDismiss,
}) => {
  const { icon: Icon, label, iconColor } = getFaultDisplay(req);
  const statusLabel = STATUS_LABEL[req.status] ?? req.status;
  const statusStyle = STATUS_PILL[req.status] ?? STATUS_PILL.NEW!;
  // OPS-38.5.5: "can the guest cancel?" is the inverse of "is it resolved?".
  const canCancel = !isRequestResolved(req.status);

  return (
    <div
      className={[
        'rounded-xl transition-colors duration-150 overflow-hidden',
        focused ? 'bg-white/10 ring-1 ring-white/20' : 'bg-white/5 hover:bg-white/[0.08]',
      ].join(' ')}
    >
      {/* Main row */}
      <div className="flex items-start gap-3 px-3 py-1.5">
        <div className="mt-0.5 shrink-0">
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-white font-medium text-sm">{label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusStyle}`}>
              {statusLabel}
            </span>
          </div>
          {req.description && (
            <p className="text-white/50 text-xs mt-0.5 line-clamp-2">{req.description}</p>
          )}
          <p className="text-white/30 text-xs mt-1">{formatTimeAgo(req.createdAt)}</p>
        </div>
        {canCancel && (
          <button
            onClick={onRequestCancel}
            aria-label="Cancel this report"
            className="shrink-0 mt-0.5 p-1.5 rounded-lg text-white/25 hover:text-red-400 hover:bg-red-900/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Inline cancel confirm */}
      {cancelPending && (
        <div className="px-3 pb-2 flex items-center gap-2">
          <span className="text-white/60 text-xs flex-1">Cancel this report?</span>
          <button
            onClick={onCancelDismiss}
            className="text-xs px-3 py-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/15 transition-colors"
          >
            Keep
          </button>
          <button
            onClick={onCancelConfirm}
            disabled={cancelling}
            className="text-xs px-3 py-1.5 rounded-lg bg-red-900/50 border border-red-700/50 text-red-300 hover:bg-red-800/60 disabled:opacity-50 transition-colors flex items-center gap-1.5"
          >
            {cancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Yes, cancel
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const MyRequestsView = forwardRef<MyRequestsViewRef, MyRequestsViewProps>(
  ({ onClose, onRequestsVoiceCommandReady }, ref) => {
    const { stay, room, guest } = useData();
    const { requests, loading, error, refetch } = useMyRequests();

    const [submittingFault, setSubmittingFault] = useState<string | null>(null);
    const [sentFaults, setSentFaults] = useState<Set<string>>(new Set());

    // Submit-confirmation flow
    const [pendingSubmitFault, setPendingSubmitFault] = useState<FaultCategory | null>(null);
    const [confirmingSubmit, setConfirmingSubmit] = useState(false);

    // Remote navigation state
    const [remoteZone, setRemoteZone] = useState<'tiles' | 'list' | null>(null);
    const [tileIndex, setTileIndex] = useState(0);          // 0-8 within the fault grid
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null); // list item

    // Cancel flow
    const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
    const [cancellingId, setCancellingId] = useState<string | null>(null);

    const stateRef = useRef({ focusedIndex, tileIndex, remoteZone, pendingCancelId, pendingSubmitFault });
    stateRef.current = { focusedIndex, tileIndex, remoteZone, pendingCancelId, pendingSubmitFault };

    // Only show MAINTENANCE requests
    const maintenanceRequests = requests.filter((r) => r.category === 'MAINTENANCE');
    const maintenanceRef = useRef(maintenanceRequests);
    maintenanceRef.current = maintenanceRequests;

    // -----------------------------------------------------------------------
    // Submit a fault report
    // -----------------------------------------------------------------------
    const submitFault = useCallback(
      async (fault: FaultCategory) => {
        if (!stay || !room || !guest) {
          console.warn('[YourRoomView] Cannot submit — missing stay/room/guest');
          return;
        }
        setSubmittingFault(fault.id);
        try {
          await createGuestRequest({
            stayId: stay.stayId,
            roomNumber: room.roomNumber,
            guestName: `${guest.firstName} ${guest.lastName}`.trim(),
            category: GuestRequestCategory.MAINTENANCE,
            description: fault.description,
          });
          setSentFaults((prev) => new Set(prev).add(fault.id));
          refetch();
          setTimeout(() => {
            setSentFaults((prev) => {
              const next = new Set(prev);
              next.delete(fault.id);
              return next;
            });
          }, 2500);
        } catch (err) {
          console.error('[YourRoomView] submitFault failed:', err);
        } finally {
          setSubmittingFault(null);
        }
      },
      [stay, room, guest, refetch]
    );

    // -----------------------------------------------------------------------
    // Confirm / dismiss a pending fault submission
    // -----------------------------------------------------------------------
    const confirmSubmit = useCallback(async () => {
      const fault = stateRef.current.pendingSubmitFault;
      if (!fault || confirmingSubmit) return;
      setPendingSubmitFault(null);
      setConfirmingSubmit(true);
      await submitFault(fault);
      setConfirmingSubmit(false);
    }, [submitFault, confirmingSubmit]);

    const dismissSubmit = useCallback(() => {
      setPendingSubmitFault(null);
    }, []);

    // -----------------------------------------------------------------------
    // Cancel a fault report — deletes it entirely
    // -----------------------------------------------------------------------
    const cancelRequest = useCallback(
      async (requestId: string) => {
        setCancellingId(requestId);
        try {
          await deleteGuestRequest(requestId);
          setPendingCancelId(null);
          refetch();
        } catch (err) {
          console.error('[YourRoomView] cancelRequest failed:', err);
        } finally {
          setCancellingId(null);
        }
      },
      [refetch]
    );

    // -----------------------------------------------------------------------
    // D-pad / keyboard remote navigation
    // -----------------------------------------------------------------------
    const TILE_COLS = 3;
    const TILE_COUNT = FAULT_CATEGORIES.length; // 9

    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        const { remoteZone: zone, tileIndex: ti, focusedIndex: fi, pendingCancelId: pci } =
          stateRef.current;
        const reqs = maintenanceRef.current;

        if (e.key === 'Escape') {
          if (stateRef.current.pendingSubmitFault) { setPendingSubmitFault(null); e.preventDefault(); return; }
          if (pci) { setPendingCancelId(null); e.preventDefault(); return; }
          onClose(); e.preventDefault(); return;
        }

        // Intercept Enter to confirm a pending fault submission
        if (e.key === 'Enter' && stateRef.current.pendingSubmitFault) {
          confirmSubmit();
          e.preventDefault();
          return;
        }

        if (
          !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)
        ) return;
        e.preventDefault();

        // Activate remote nav on first key press
        if (zone === null) {
          setRemoteZone('tiles');
          setTileIndex(0);
          return;
        }

        if (zone === 'tiles') {
          const row = Math.floor(ti / TILE_COLS);
          const col = ti % TILE_COLS;
          const totalRows = Math.ceil(TILE_COUNT / TILE_COLS);

          switch (e.key) {
            case 'ArrowLeft':  setTileIndex(Math.max(0, ti - 1)); break;
            case 'ArrowRight': setTileIndex(Math.min(TILE_COUNT - 1, ti + 1)); break;
            case 'ArrowUp': {
              if (row > 0) setTileIndex(Math.min((row - 1) * TILE_COLS + col, TILE_COUNT - 1));
              break;
            }
            case 'ArrowDown': {
              if (row < totalRows - 1) {
                setTileIndex(Math.min((row + 1) * TILE_COLS + col, TILE_COUNT - 1));
              } else if (reqs.length > 0) {
                // Move to list zone
                setRemoteZone('list');
                setFocusedIndex(0);
              }
              break;
            }
            case 'Enter': {
              const fault = FAULT_CATEGORIES[ti];
              if (fault) setPendingSubmitFault(fault);
              break;
            }
          }
          return;
        }

        if (zone === 'list') {
          const listLen = reqs.length;
          const idx = fi ?? 0;
          const req = reqs[idx];

          // Cancel-confirm is open
          if (pci) {
            switch (e.key) {
              case 'ArrowLeft':  setPendingCancelId(null); break;
              case 'ArrowRight': break; // already on confirm
              case 'Enter':      if (req && pci === req.id) cancelRequest(req.id); break;
            }
            return;
          }

          switch (e.key) {
            case 'ArrowUp': {
              if (idx <= 0) {
                // Jump back to tile grid
                setRemoteZone('tiles');
                setFocusedIndex(null);
                setTileIndex(TILE_COUNT - TILE_COLS); // start of last row
              } else {
                setFocusedIndex(idx - 1);
              }
              break;
            }
            case 'ArrowDown': {
              if (idx < listLen - 1) setFocusedIndex(idx + 1);
              break;
            }
            case 'ArrowRight': {
              // Show cancel confirm if applicable
              if (req && !isRequestResolved(req.status)) setPendingCancelId(req.id);
              break;
            }
            case 'ArrowLeft': {
              setPendingCancelId(null);
              break;
            }
            case 'Enter': {
              // Enter on a list item: show cancel confirm (if cancellable)
              if (req && !isRequestResolved(req.status)) setPendingCancelId(req.id);
              break;
            }
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, submitFault, cancelRequest, confirmSubmit, dismissSubmit]);

    // -----------------------------------------------------------------------
    // Voice command handler
    // -----------------------------------------------------------------------
    const handleVoiceCommand = useCallback<MyRequestsViewRef['handleVoiceCommand']>(
      (command) => {
        const { type, payload } = command as { type: string; payload?: unknown };
        const { focusedIndex: fi, pendingSubmitFault: psf } = stateRef.current;
        const mReqs = maintenanceRef.current;

        switch (type) {
          case 'BACK':
            onClose();
            return;

          case 'COUNT':
            if (mReqs.length === 0) return 'You have no reported issues yet.';
            return `You have ${mReqs.length} reported ${mReqs.length === 1 ? 'issue' : 'issues'}.`;

          case 'NEXT_REQUEST': {
            if (mReqs.length === 0) return 'No issues to navigate.';
            const next = fi === null ? 0 : Math.min(fi + 1, mReqs.length - 1);
            setFocusedIndex(next);
            setRemoteZone('list');
            const reqNext = mReqs[next]!;
            const { label } = getFaultDisplay(reqNext);
            const status = STATUS_LABEL[reqNext.status] ?? reqNext.status;
            return `${label}, ${status}. ${formatTimeAgo(reqNext.createdAt)}.`;
          }

          case 'PREV_REQUEST': {
            if (mReqs.length === 0) return 'No issues to navigate.';
            const prev = fi === null ? 0 : Math.max(fi - 1, 0);
            setFocusedIndex(prev);
            setRemoteZone('list');
            const reqPrev = mReqs[prev]!;
            const { label } = getFaultDisplay(reqPrev);
            const status = STATUS_LABEL[reqPrev.status] ?? reqPrev.status;
            return `${label}, ${status}. ${formatTimeAgo(reqPrev.createdAt)}.`;
          }

          case 'FIRST_REQUEST':
            if (mReqs.length === 0) return 'No issues.';
            setFocusedIndex(0); setRemoteZone('list');
            return `First issue: ${getFaultDisplay(mReqs[0]!).label}.`;

          case 'LAST_REQUEST':
            if (mReqs.length === 0) return 'No issues.';
            setFocusedIndex(mReqs.length - 1); setRemoteZone('list');
            return `Last issue: ${getFaultDisplay(mReqs[mReqs.length - 1]!).label}.`;

          case 'GO_TO_INDEX': {
            const idx = typeof payload === 'number' ? payload - 1 : 0;
            if (idx < 0 || idx >= mReqs.length) return `No issue at position ${idx + 1}.`;
            setFocusedIndex(idx); setRemoteZone('list');
            return `Issue ${idx + 1}: ${getFaultDisplay(mReqs[idx]!).label}.`;
          }

          case 'READ_REQUEST': {
            const i = fi ?? 0;
            if (mReqs.length === 0) return 'You have no reported issues.';
            const req = mReqs[i]!;
            const { label } = getFaultDisplay(req);
            const status = STATUS_LABEL[req.status] ?? req.status;
            return `${label}: ${req.description}. Status: ${status}. Reported ${formatTimeAgo(req.createdAt)}.`;
          }

          case 'STATUS': {
            const i = fi ?? 0;
            if (mReqs.length === 0) return 'You have no reported issues.';
            const reqStatus = mReqs[i]!;
            const status = STATUS_LABEL[reqStatus.status] ?? reqStatus.status;
            return `Issue ${i + 1} of ${mReqs.length}. Status: ${status}.`;
          }

          case 'CANCEL_REQUEST': {
            const i = fi ?? 0;
            if (mReqs.length === 0) return 'You have no reported issues to cancel.';
            const req = mReqs[i]!;
            if (isRequestResolved(req.status)) return `That issue is already resolved and cannot be cancelled.`;
            cancelRequest(req.id);
            const { label } = getFaultDisplay(req);
            return `Cancelling your ${label} report.`;
          }

          case 'REPORT_FAULT': {
            const faultPayload = payload as { faultId?: string } | undefined;
            const faultId = faultPayload?.faultId;
            const fault = FAULT_CATEGORIES.find((f) => f.id === faultId);
            if (!fault) return 'Sorry, I could not find that issue type.';
            setPendingSubmitFault(fault);
            return `Ready to report ${fault.label}. Say confirm to send, or cancel to dismiss.`;
          }

          case 'CONFIRM_SUBMIT': {
            if (!psf) return 'Nothing to confirm right now.';
            const faultLabel = psf.label;
            confirmSubmit();
            return `Reporting ${faultLabel} issue. Maintenance will be notified.`;
          }

          case 'DISMISS_SUBMIT': {
            if (!psf) return; // nothing pending — silent no-op
            setPendingSubmitFault(null);
            return 'Report dismissed.';
          }

          case 'NEXT_COLUMN':
          case 'PREV_COLUMN':
          case 'GO_TO_COLUMN':
          case 'EXPAND':
          case 'COLLAPSE':
            return;

          default:
            return;
        }
      },
      [onClose, cancelRequest]
    );

    useImperativeHandle(ref, () => ({ handleVoiceCommand }), [handleVoiceCommand]);

    React.useEffect(() => {
      if (onRequestsVoiceCommandReady) {
        onRequestsVoiceCommandReady(handleVoiceCommand);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRequestsVoiceCommandReady]);

    // Sync focusedIndex to stateRef (already done via stateRef.current assignment above)

    const voiceHint = pendingSubmitFault
      ? `OK to confirm · ESC to cancel`
      : remoteZone === 'tiles'
      ? 'D-pad to select · OK to queue'
      : remoteZone === 'list'
      ? 'D-pad to navigate · ► to cancel'
      : null;

    return (
      <div className="flex flex-col h-full bg-black/40 backdrop-blur-sm text-white select-none overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 shrink-0">
          <div>
            <h2 className="text-xl font-bold tracking-tight">My Room</h2>
            <p className="text-white/40 text-xs mt-0.5">Report an issue and we'll handle it</p>
          </div>
          <div className="flex items-center gap-2">
            {voiceHint && (
              <span className="text-white/30 text-xs hidden sm:flex items-center gap-1">
                {voiceHint}
              </span>
            )}
            <button
              onClick={() => refetch()}
              aria-label="Refresh"
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">

          {/* Fault grid */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
              What needs attention?
            </p>
            <div className="grid grid-cols-3 gap-2.5">
              {FAULT_CATEGORIES.map((fault, idx) => (
                <FaultTile
                  key={fault.id}
                  fault={fault}
                  submitting={submittingFault === fault.id}
                  justSent={sentFaults.has(fault.id)}
                  remoteFocused={remoteZone === 'tiles' && tileIndex === idx}
                  isPending={pendingSubmitFault?.id === fault.id}
                  onTap={() => setPendingSubmitFault(fault)}
                />
              ))}
            </div>
          </section>

          {/* Submit confirmation card */}
          {pendingSubmitFault && (() => {
            const PendingIcon = pendingSubmitFault.icon;
            return (
              <div className="rounded-xl bg-amber-950/40 border border-amber-600/40 ring-1 ring-amber-500/20 p-4 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="shrink-0 p-2 rounded-lg bg-amber-900/40">
                  <PendingIcon className={`w-5 h-5 ${pendingSubmitFault.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">
                    Report {pendingSubmitFault.label}?
                  </p>
                  <p className="text-xs text-white/50 mt-0.5">
                    Maintenance will be notified immediately
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={dismissSubmit}
                    className="text-xs px-3 py-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/15 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSubmit}
                    disabled={confirmingSubmit}
                    className="text-xs px-3 py-1.5 rounded-lg bg-amber-700/70 border border-amber-500/50 text-white hover:bg-amber-600/80 disabled:opacity-50 transition-colors flex items-center gap-1.5 font-medium"
                  >
                    {confirmingSubmit
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <CheckCircle className="w-3 h-3" />}
                    Yes, report
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Timeline */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
              Reported Issues
            </p>

            {error && (
              <div className="text-red-400 text-sm bg-red-900/20 border border-red-700/40 rounded-xl p-4">
                {error}
              </div>
            )}

            {loading && !error && (
              <div className="space-y-3">
                {[1, 2].map((n) => (
                  <div key={n} className="h-16 rounded-xl bg-white/5 animate-pulse" />
                ))}
              </div>
            )}

            {!loading && !error && maintenanceRequests.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-white/30">
                <Wrench className="w-8 h-8 mb-3 opacity-40" />
                <p className="text-sm font-medium">Nothing reported</p>
                <p className="text-xs mt-1 text-center">
                  Tap an issue above to alert our team
                </p>
              </div>
            )}

            {!loading && !error && maintenanceRequests.length > 0 && (
              <div className="space-y-2">
                {maintenanceRequests.map((req, idx) => (
                  <TimelineItem
                    key={req.id}
                    req={req}
                    focused={remoteZone === 'list' && focusedIndex === idx}
                    cancelPending={pendingCancelId === req.id}
                    cancelling={cancellingId === req.id}
                    onRequestCancel={() => setPendingCancelId(req.id)}
                    onCancelConfirm={() => cancelRequest(req.id)}
                    onCancelDismiss={() => setPendingCancelId(null)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }
);

MyRequestsView.displayName = 'YourRoomView';

export { MyRequestsView };
export default MyRequestsView;
